import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowLeft, ArrowUpRight, Maximize2 } from "lucide-react";
import { getCaseStudy } from "@/data/caseStudies";
import Footer from "@/components/Footer";
import { SubNav, NotFound, ComingSoon } from "@/components/case-study/CaseChrome";
import { PhoneFrame, Lines, SectionHead, CaseImage } from "@/components/case-study/CaseMedia";
import WorkBlock from "@/components/case-study/WorkBlock";
import Lightbox from "@/components/case-study/Lightbox";

export default function CaseStudy() {
  const { slug } = useParams();
  const s = getCaseStudy(slug);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    document.body.style.overflow = lightbox ? "hidden" : "";
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox]);

  if (!s) return <NotFound />;
  if (s.comingSoon) return <ComingSoon s={s} />;

  const thumbOf = (src) => src.replace(/\.webp$/, "-thumb.webp");

  return (
    <div
      className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]"
      data-testid="case-study-page"
    >
      <SubNav />

      <main>
        {/* Hero — lead with mobile */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
              <p className="label-mono text-[#595959]">/ {s.id} — Case study</p>
              <p className="label-mono text-[#595959]">{s.client}</p>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter">
              {s.title}
            </h1>
            <p className="font-display text-2xl md:text-3xl tracking-tighter text-[#4C1D95] mt-4 max-w-3xl">
              {s.subtitle}
            </p>
            <p className="max-w-3xl text-lg md:text-xl leading-relaxed mt-8 text-[#0A0A0A]">
              {s.intro}
            </p>
          </div>

          {/* Mobile app-screen montage */}
          {s.heroMobile && (
            <div className="border-t border-[#0A0A0A] bg-[#EDE9FE]">
              <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-14">
                <p className="label-mono text-[#4C1D95] mb-6">Mobile app</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 max-w-3xl mx-auto">
                  {s.heroMobile.map((img, i) => (
                    <figure key={img.src} className="flex flex-col">
                      <PhoneFrame
                        src={img.src}
                        alt={img.alt}
                        testid={`hero-mobile-${i}`}
                        eager
                      />
                      <figcaption className="label-mono text-[#595959] mt-3 text-center">
                        {img.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Overview — text + 2×2 contained mockups, two columns */}
          {s.heroImages && (
            <div className="border-t border-[#0A0A0A] bg-[#EDE9FE]">
              <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-14 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                  {/* Left — summary text */}
                  <div className="lg:col-span-4">
                    <p className="label-mono text-[#4C1D95] mb-5">Overview</p>
                    {s.heroCaption && (
                      <p className="text-lg md:text-xl leading-relaxed text-[#0A0A0A]">
                        {s.heroCaption}
                      </p>
                    )}
                  </div>

                  {/* Right — 2-column mockup grid */}
                  <div className="lg:col-span-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                      {s.heroImages.map((img, i) => (
                        <figure key={img.src}>
                          <button
                            type="button"
                            onClick={() => setLightbox(img)}
                            className="group block w-full"
                            data-testid={`hero-image-${i}`}
                          >
                          <div className="relative aspect-[3/4] overflow-hidden border border-[#0A0A0A] bg-white">
                            <img
                              src={img.src}
                              alt={img.alt}
                              loading={i < 2 ? "eager" : "lazy"}
                              className="absolute inset-0 w-full h-full object-contain p-3 md:p-4 transition-transform duration-500 group-hover:scale-[1.03]"
                            />
                            <span className="absolute top-2 right-2 z-10 label-mono bg-white/90 border border-[#0A0A0A] px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              View <Maximize2 size={11} strokeWidth={1.5} />
                            </span>
                          </div>
                          </button>
                          {img.label && (
                            <figcaption className="label-mono text-[#595959] mt-3">
                              {img.label}
                            </figcaption>
                          )}
                        </figure>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* 01 — Overview: Problem / Solution */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#595959] mb-8">/ 01 — Overview</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-l border-[#0A0A0A]">
              <div
                className="border-b border-r border-[#0A0A0A] p-8 md:p-10"
                data-testid="overview-problem"
              >
                <p className="label-mono text-[#4C1D95] mb-4">Problem</p>
                <p className="text-lg leading-relaxed">{s.overview.problem}</p>
              </div>
              <div
                className="border-b border-r border-[#0A0A0A] p-8 md:p-10 bg-[#EDE9FE]"
                data-testid="overview-solution"
              >
                <p className="label-mono text-[#4C1D95] mb-4">Solution</p>
                <p className="text-lg leading-relaxed">{s.overview.solution}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 02 — Project Details / At a Glance (compact strip) */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-10 md:py-12">
            <p className="label-mono text-[#595959] mb-5">
              / 02 — Project details / at a glance
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0A0A0A]">
              {s.details.map((d, i) => (
                <div
                  key={d.k}
                  className="border-b border-r border-[#0A0A0A] px-4 py-3 flex flex-col gap-1"
                  data-testid={`detail-${i}`}
                >
                  <dt className="label-mono text-[#595959]">{d.k}</dt>
                  <dd className="text-sm leading-snug">{d.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* 03 — My Focus */}
        <section className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-[#FFFFFF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28 grid grid-cols-12 gap-y-8">
            <div className="col-span-12 lg:col-span-4">
              <p className="label-mono text-[#A78BFA] mb-4">
                / 03 — {s.focusKicker || "My focus"}
              </p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter">
                {s.focusHeading ? (
                  <Lines lines={s.focusHeading} accentClass="text-[#A78BFA]" />
                ) : (
                  <>
                    Optimizing the
                    <br /> <span className="text-[#A78BFA]">business</span>,
                    <br /> not the UI.
                  </>
                )}
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-8 lg:pl-16 text-lg md:text-xl leading-relaxed text-[#DDD6FE]">
              {s.focus}
            </p>
          </div>
        </section>

        {/* 04 — Understanding the Three User Types */}
        {s.userTypes && (
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28">
            <SectionHead
              index="04"
              kicker={s.userTypes.kicker || "Understanding the user types"}
            >
              {s.userTypes.heading ? (
                <Lines lines={s.userTypes.heading} />
              ) : (
                <>
                  Three roles,
                  <br /> one <span className="text-[#4C1D95]">system</span>.
                </>
              )}
            </SectionHead>
            <p className="max-w-2xl text-lg md:text-xl leading-relaxed mb-12">
              {s.userTypes.intro}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#0A0A0A] mb-16">
              {s.userTypes.roles.map((r, i) => (
                <div
                  key={r.role}
                  className={`border-b border-r border-[#0A0A0A] p-6 md:p-8 flex flex-col ${
                    r.highlight ? "bg-[#4C1D95] text-[#FFFFFF]" : "bg-white"
                  }`}
                  data-testid={`user-type-${i}`}
                >
                  <div className="font-display text-4xl mb-4">0{i + 1}</div>
                  <h3 className="font-display text-2xl tracking-tighter mb-3 leading-tight">
                    {r.role}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed ${
                      r.highlight ? "text-[#EDE9FE]" : "text-[#595959]"
                    }`}
                  >
                    {r.desc}
                  </p>
                </div>
              ))}
            </div>

            {s.userTypes.map && (
              <figure>
                <CaseImage img={s.userTypes.map} testid="system-flow-map" />
                <figcaption className="text-sm text-[#595959] leading-relaxed mt-4 max-w-2xl font-mono-ibm">
                  {s.userTypes.map.caption}
                </figcaption>
              </figure>
            )}
          </div>
        </section>
        )}

        {/* 05 — The Work */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-20 md:pt-28">
            <p className="label-mono text-[#595959]">/ 05 — The work</p>
          </div>

          {s.work.map((block, bi) => (
            <WorkBlock
              key={block.heading}
              block={block}
              bi={bi}
              thumbOf={thumbOf}
              onOpen={setLightbox}
            />
          ))}
        </section>

        {/* 06 — Key Decisions */}
        <section className="border-b border-[#0A0A0A] bg-[#EDE9FE]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28">
            <SectionHead
              index="06"
              kicker={s.keyDecisions.kicker || "Key decisions"}
            >
              {s.keyDecisions.heading ? (
                <Lines lines={s.keyDecisions.heading} />
              ) : (
                <>
                  Decisions that
                  <br /> shaped the <span className="text-[#4C1D95]">system</span>.
                </>
              )}
            </SectionHead>
            <p className="max-w-2xl text-lg md:text-xl leading-relaxed mb-12">
              {s.keyDecisions.intro}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#0A0A0A]">
              {s.keyDecisions.points.map((p, i) => (
                <div
                  key={p.t}
                  className="border-b border-r border-[#0A0A0A] p-6 md:p-8 bg-white flex flex-col"
                  data-testid={`key-decision-${i}`}
                >
                  <div className="font-display text-5xl mb-5 text-[#4C1D95]">
                    0{i + 1}
                  </div>
                  <h3 className="font-display text-2xl tracking-tighter mb-3 leading-tight">
                    {p.t}
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed text-[#595959]">
                    {p.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 07 — Outcome */}
        <section className="border-b border-[#0A0A0A]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28 grid grid-cols-12 gap-y-8">
            <div className="col-span-12 lg:col-span-4">
              <SectionHead index="07" kicker={s.outcomeKicker || "Outcome"}>
                {s.outcomeHeading ? (
                  <Lines lines={s.outcomeHeading} />
                ) : (
                  <>
                    How it works,
                    <br /> as a business.
                  </>
                )}
              </SectionHead>
            </div>
            <p className="col-span-12 lg:col-span-8 lg:pl-16 text-lg md:text-xl leading-relaxed">
              {s.outcome}
            </p>
            {s.outcomeStats && (
              <div className="col-span-12 mt-4 grid grid-cols-1 sm:grid-cols-3 border-t border-l border-[#0A0A0A]">
                {s.outcomeStats.map((st, i) => (
                  <div
                    key={st.k}
                    className="border-b border-r border-[#0A0A0A] p-6 md:p-8"
                    data-testid={`outcome-stat-${i}`}
                  >
                    <div className="font-display text-4xl md:text-5xl tracking-tighter text-[#4C1D95] mb-2">
                      {st.v}
                    </div>
                    <div className="text-sm text-[#595959] leading-relaxed">
                      {st.k}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 08 — Reflection */}
        <section className="border-b border-[#0A0A0A] bg-[#4C1D95] text-[#FFFFFF]">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28">
            <p className="label-mono mb-6 text-[#DDD6FE]">/ 08 — Reflection</p>
            <p className="font-display text-3xl md:text-5xl tracking-tighter max-w-4xl leading-[1.05]">
              {s.reflection}
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#FFFFFF] px-6 py-4 label-mono hover:bg-white hover:text-[#0A0A0A] transition-colors"
                data-testid="reflection-cta-book"
              >
                {s.reflectionCtaLabel || "Book your audit"}{" "}
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/#work"
                className="inline-flex items-center gap-2 border border-[#FFFFFF] px-6 py-4 label-mono hover:bg-[#FFFFFF] hover:text-[#4C1D95] transition-colors"
                data-testid="reflection-cta-back"
              >
                <ArrowLeft size={16} strokeWidth={1.5} /> See more work
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />

      <Footer />
    </div>
  );
}
